import { Component, Input, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";

export type AvatarSize = "small" | "medium" | "large" | "extra-large";

@Component({
  selector: "app-avatar-initials",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./avatar-initials.component.html",
  styleUrls: ["./avatar-initials.component.scss"],
})
export class AvatarInitialsComponent implements OnInit {
  @Input() firstName: string = "";
  @Input() lastName: string = "";
  @Input() size: AvatarSize = "medium";
  @Input() customText: string = ""; // For custom initials override

  initials: string = "";
  backgroundColor: string = "";

  // Material Design color palette for avatars
  private readonly colors = [
    "#f44336", // Red
    "#e91e63", // Pink
    "#9c27b0", // Purple
    "#673ab7", // Deep Purple
    "#3f51b5", // Indigo
    "#2196f3", // Blue
    "#03a9f4", // Light Blue
    "#00bcd4", // Cyan
    "#009688", // Teal
    "#4caf50", // Green
    "#8bc34a", // Light Green
    "#ff9800", // Orange
    "#ff5722", // Deep Orange
    "#795548", // Brown
    "#607d8b", // Blue Grey
  ];

  ngOnInit(): void {
    this.generateInitials();
    this.generateBackgroundColor();
  }

  private generateInitials(): void {
    if (this.customText) {
      this.initials = this.customText.substring(0, 2).toUpperCase();
      return;
    }

    const firstInitial = this.firstName?.charAt(0)?.toUpperCase() || "";
    const lastInitial = this.lastName?.charAt(0)?.toUpperCase() || "";

    if (firstInitial && lastInitial) {
      this.initials = firstInitial + lastInitial;
    } else if (firstInitial) {
      this.initials = firstInitial;
    } else if (lastInitial) {
      this.initials = lastInitial;
    } else {
      this.initials = "?"; // Fallback
    }
  }

  private generateBackgroundColor(): void {
    // Generate consistent color based on name hash
    const name = (this.firstName + this.lastName).toLowerCase();
    if (!name) {
      this.backgroundColor = "#9e9e9e"; // Default grey
      return;
    }

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    const colorIndex = Math.abs(hash) % this.colors.length;
    this.backgroundColor = this.colors[colorIndex];
  }

  get ariaLabel(): string {
    if (this.customText) {
      return `Avatar with initials ${this.customText}`;
    }

    const fullName = `${this.firstName} ${this.lastName}`.trim();
    return fullName ? `Avatar for ${fullName}` : "User avatar";
  }

  get sizeClass(): string {
    return `avatar-${this.size}`;
  }
}
